import React, { useEffect, useState } from 'react'
import { Container, Modal, ModalBody, ModalFooter, ModalHeader, Button } from 'reactstrap'
import { useNavigate } from 'react-router-dom'
import Header from '../components/Header'
import InstancesHeader from '../components/home/InstancesHeader'
import InstancesGrid from '../components/home/InstancesGrid'
import InstancesPagination from '../components/home/InstancesPagination'
import servicio from '../services/apiInstanceService'
import '../styles/HomeView.css'

function HomeView() {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(4)
  const [refreshKey, setRefreshKey] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null, status: null, name: '' })

  const handlePageSizeChange = (size) => {
    setItemsPerPage(size)
    setCurrentPage(1)
  }

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value)
  }

  const handleSearch = () => {
    setSearchTerm(searchInput.trim())
    setCurrentPage(1)
  }

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1)
  }

  useEffect(() => {
    let isMounted = true

    const loadInstances = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await servicio.listarInstancias(
          currentPage - 1,
          itemsPerPage,
          searchTerm
        )
        if (isMounted) {
          const content = Array.isArray(data?.content) ? data.content : []
          const pageInfo = data?.page || {}
          setItems(content)
          setTotalPages(typeof pageInfo?.totalPages === 'number' ? pageInfo.totalPages : 0)
        }
      } catch (err) {
        if (isMounted) {
          setError('No se pudieron cargar las instancias.')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadInstances()
    return () => {
      isMounted = false
    }
  }, [currentPage, itemsPerPage, refreshKey, searchTerm])

  const handleAddClick = () => {
    navigate('/add')
  }

  const handleEdit = (id) => {
    const instancia = items.find((item) => item.id === id)
    navigate('/edit', { state: { instancia } })
  }

  const handleToggleBlock = (id, currentState) => {
    const instancia = items.find((item) => item.id === id)
    setConfirmModal({
      isOpen: true,
      id,
      status: currentState,
      name: instancia?.name || '',
    })
  }

  const handleConfirmToggle = async () => {
    if (!confirmModal.id || !confirmModal.status) {
      setConfirmModal({ isOpen: false, id: null, status: null, name: '' })
      return
    }

    const newState = confirmModal.status === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE'
    setError(null)
    try {
      await servicio.actualizarInstancia(confirmModal.id, { status: newState })
      setRefreshKey((prev) => prev + 1)
    } catch (err) {
      setError('No se pudo actualizar el estado de la instancia.')
    } finally {
      setConfirmModal({ isOpen: false, id: null, status: null, name: '' })
    }
  }

  const handleCancelToggle = () => {
    setConfirmModal({ isOpen: false, id: null, status: null, name: '' })
  }

  const handleDelete = async () => {
    alert('Funcionalidad de eliminacion no implementada aun.')
  }

  const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getEndOfMonth = () => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth() + 1, 0)
  }

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const handleExport = async (format) => {
    setIsExporting(true)
    setError(null)
    try {
      const fechaReferencia = formatDate(getEndOfMonth())
      if (format === 'excel') {
        const blob = await servicio.exportarInstanciasExcel(fechaReferencia)
        downloadBlob(blob, `reporte-instancias-${fechaReferencia}.xlsx`)
      } else {
        const blob = await servicio.exportarInstanciasPdf(fechaReferencia)
        downloadBlob(blob, `reporte-instancias-${fechaReferencia}.pdf`)
      }
    } catch (err) {
      setError('No se pudo exportar el reporte.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <Header />
      <Container fluid className="home-container">
        <Container>
          <InstancesHeader
            onAdd={handleAddClick}
            onExportExcel={() => handleExport('excel')}
            onExportPdf={() => handleExport('pdf')}
            isExporting={isExporting}
          />
          {isLoading && <p>Cargando instancias...</p>}
          {error && <p className="text-danger">{error}</p>}
          {!isLoading && !error && (
            <>
              <InstancesGrid
                items={items}
                pageSize={itemsPerPage}
                onPageSizeChange={handlePageSizeChange}
                onRefresh={handleRefresh}
                searchValue={searchInput}
                onSearchChange={handleSearchChange}
                onSearch={handleSearch}
                onEdit={handleEdit}
                onToggleBlock={handleToggleBlock}
                onDelete={handleDelete}
              />
              <InstancesPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </Container>
      </Container>

      <Modal
        isOpen={confirmModal.isOpen}
        toggle={handleCancelToggle}
        centered
        className="instance-confirm-modal"
      >
        <ModalHeader toggle={handleCancelToggle} className="instance-confirm-title">
          {confirmModal.status === 'ACTIVE' ? 'Bloquear instancia' : 'Desbloquear instancia'}
        </ModalHeader>
        <ModalBody>
          <p className="instance-confirm-text">
            {confirmModal.status === 'ACTIVE'
              ? 'Seguro que deseas bloquear esta instancia?'
              : 'Seguro que deseas desbloquear esta instancia?'}
          </p>
          {confirmModal.name && (
            <p className="instance-confirm-name">
              <strong>Instancia:</strong> {confirmModal.name}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button className="btn-modal-cancel" onClick={handleCancelToggle}>
            Cancelar
          </Button>
          <Button
            className={confirmModal.status === 'ACTIVE' ? 'btn-modal-warning' : 'btn-modal-success'}
            onClick={handleConfirmToggle}
          >
            {confirmModal.status === 'ACTIVE' ? 'Bloquear' : 'Desbloquear'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default HomeView
